/**
 * @fileoverview Shared plan configuration for KaraoNow.
 * @summary Centralized subscription plans with limits for both backend and frontend.
 *
 * @package @karaonow/shared
 * @module constants/plans
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
/**
 * @summary Subscription plan type enumeration.
 * @type {enum}
 */
export var SubscriptionType;
(function (SubscriptionType) {
    SubscriptionType["FREE"] = "free";
    SubscriptionType["ONE_SHOT"] = "one_shot";
    SubscriptionType["PRO"] = "pro";
    SubscriptionType["PREMIUM"] = "premium";
    SubscriptionType["ULTIMATE"] = "ultimate";
})(SubscriptionType || (SubscriptionType = {}));
/**
 * @summary Subscription type values as array (for Mongoose enums).
 * Automatically derived from SubscriptionType enum to prevent duplication.
 */
export const SUBSCRIPTION_TYPE_VALUES = Object.values(SubscriptionType).filter(v => typeof v === 'string');
/**
 * @summary Payment type enumeration (for database records).
 * @type {enum}
 */
export var PaymentType;
(function (PaymentType) {
    PaymentType["SINGLE_EVENT"] = "single_event";
    PaymentType["MONTHLY_SUBSCRIPTION"] = "monthly_subscription";
})(PaymentType || (PaymentType = {}));
/**
 * @summary Payment type values as array (for Mongoose enums).
 * Automatically derived from PaymentType enum to prevent duplication.
 */
export const PAYMENT_TYPE_VALUES = Object.values(PaymentType).filter(v => typeof v === 'string');
/**
 * @summary Subscription status enumeration.
 * @type {enum}
 */
export var SubscriptionStatus;
(function (SubscriptionStatus) {
    SubscriptionStatus["ACTIVE"] = "active";
    SubscriptionStatus["CANCELED"] = "canceled";
    SubscriptionStatus["EXPIRED"] = "expired";
})(SubscriptionStatus || (SubscriptionStatus = {}));
/**
 * @summary Subscription status values as array (for Mongoose enums).
 * Automatically derived from SubscriptionStatus enum to prevent duplication.
 */
export const SUBSCRIPTION_STATUS_VALUES = Object.values(SubscriptionStatus).filter(v => typeof v === 'string');
/**
 * @summary Payment status enumeration.
 * @type {enum}
 */
export var PaymentStatus;
(function (PaymentStatus) {
    PaymentStatus["PENDING"] = "pending";
    PaymentStatus["COMPLETED"] = "completed";
    PaymentStatus["FAILED"] = "failed";
    PaymentStatus["REFUNDED"] = "refunded";
    PaymentStatus["ACTIVE"] = "active";
    PaymentStatus["CANCELED"] = "canceled";
    PaymentStatus["PAST_DUE"] = "past_due";
})(PaymentStatus || (PaymentStatus = {}));
/**
 * @summary Payment status values as array (for Mongoose enums).
 * Automatically derived from PaymentStatus enum to prevent duplication.
 */
export const PAYMENT_STATUS_VALUES = Object.values(PaymentStatus).filter(v => typeof v === 'string');
/**
 * @summary Subscription plans configuration.
 * @description Defines limits for each subscription type.
 * @constant
 * @type {Record<SubscriptionType, PlanLimits>}
 */
export const SUBSCRIPTION_PLANS = {
    [SubscriptionType.FREE]: {
        maxEvents: 4, // Total events (all statuses) excluding ONE_SHOT
        maxSongsPerEvent: 15,
        maxSongsInQueue: 15,
        displayName: 'Plan Gratuit',
        priceEur: null,
    },
    [SubscriptionType.ONE_SHOT]: {
        maxEvents: 1, // Single event, 15 days validity
        maxSongsPerEvent: Infinity,
        maxSongsInQueue: Infinity,
        displayName: 'Plan Événement Unique',
        priceEur: 4.99,
    },
    [SubscriptionType.PRO]: {
        maxEvents: 3, // Per month (rolling period based on billing cycle)
        maxSongsPerEvent: Infinity,
        maxSongsInQueue: Infinity,
        displayName: 'Plan Pro',
        priceEur: 9.99,
    },
    [SubscriptionType.PREMIUM]: {
        maxEvents: 5, // Per month (rolling period based on billing cycle)
        maxSongsPerEvent: Infinity,
        maxSongsInQueue: Infinity,
        displayName: 'Plan Premium',
        priceEur: 14.99,
    },
    [SubscriptionType.ULTIMATE]: {
        maxEvents: Infinity, // Unlimited
        maxSongsPerEvent: Infinity,
        maxSongsInQueue: Infinity,
        displayName: 'Plan Ultimate',
        priceEur: 24.99,
    },
};
/**
 * @summary ONE_SHOT plan validity period in days.
 * @description Number of days after purchase that a ONE_SHOT event remains valid.
 * @constant
 * @type {number}
 */
export const ONESHOT_VALIDITY_DAYS = 15;
/**
 * @summary Get limits for a specific subscription type.
 * @param {SubscriptionType} type - The subscription type.
 * @returns {PlanLimits} The plan limits.
 */
export function getPlanLimits(type) {
    return SUBSCRIPTION_PLANS[type] || SUBSCRIPTION_PLANS[SubscriptionType.FREE];
}
/**
 * @summary Get plan limits by string identifier.
 * @param {string} typeString - The subscription type as string.
 * @returns {PlanLimits} The plan limits.
 */
export function getPlanLimitsByString(typeString) {
    const type = typeString;
    return getPlanLimits(type);
}
/**
 * @summary Get plan limits enriched with Stripe Price IDs.
 * @description Centralized function to enrich plan limits with Price IDs from environment.
 * @param {SubscriptionType} planType - The subscription type.
 * @param {StripePriceIds} priceIds - Object containing Stripe Price IDs from environment.
 * @returns {PlanLimits} The plan limits with Stripe Price ID.
 */
export function getPlanLimitsWithPriceId(planType, priceIds) {
    const planLimits = getPlanLimits(planType);
    // Map plan types to their Price IDs
    let stripePriceId;
    switch (planType) {
        case SubscriptionType.PRO:
            stripePriceId = priceIds.pro;
            break;
        case SubscriptionType.PREMIUM:
            stripePriceId = priceIds.premium;
            break;
        case SubscriptionType.ULTIMATE:
            stripePriceId = priceIds.ultimate;
            break;
        case SubscriptionType.ONE_SHOT:
            stripePriceId = priceIds.oneShot;
            break;
        default:
            // FREE plan doesn't have a Price ID
            break;
    }
    return {
        ...planLimits,
        stripePriceId,
    };
}
//# sourceMappingURL=plans.js.map